/**
 * Slider for the "LS Recent Installations" section.
 *
 * The cards live in a scroll-snapping track, so CSS media queries stay the only
 * place that decides how many cards are visible. This element just measures what
 * CSS produced, pages the track by that many cards, and keeps the arrows and
 * dots in sync. The controls stay hidden whenever every card already fits, so a
 * three-card section looks exactly like the plain grid it used to be.
 */
class LsriCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lsri-track]');
    this.nav = this.querySelector('[data-lsri-nav]');
    this.prevButton = this.querySelector('[data-lsri-prev]');
    this.nextButton = this.querySelector('[data-lsri-next]');
    this.dots = this.querySelector('[data-lsri-dots]');
    this.slides = Array.from(this.querySelectorAll('[data-lsri-slide]'));

    if (!this.track || !this.nav || this.slides.length === 0) return;

    this.step = 0;
    this.perView = 1;
    this.pageCount = 0;
    this.frame = null;

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.syncControls();
      });
    };
    this.onBlockSelect = (event) => this.revealBlock(event);
    this.onPrev = () => this.page(-1);
    this.onNext = () => this.page(1);

    this.prevButton.addEventListener('click', this.onPrev);
    this.nextButton.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.track);

    this.measure();
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  get behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  /** Read the layout back out of the DOM: card width, cards per view, page count. */
  measure() {
    const width = this.slides[0].getBoundingClientRect().width;
    if (width === 0) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const previousPerView = this.perView;
    this.step = width + gap;
    this.perView = Math.max(1, Math.round(this.track.clientWidth / this.step));

    const overflows = this.maxScroll > 2;
    this.pageCount = overflows ? Math.ceil(this.slides.length / this.perView) : 0;
    this.nav.toggleAttribute('hidden', !overflows);

    this.buildDots(previousPerView !== this.perView);
    this.syncControls();
  }

  buildDots(force = false) {
    if (!this.dots || (!force && this.dots.children.length === this.pageCount)) return;

    this.dots.textContent = '';
    for (let i = 0; i < this.pageCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lsri__dot';
      dot.setAttribute('aria-label', `Go to installations ${i * this.perView + 1}–${Math.min((i + 1) * this.perView, this.slides.length)}`);
      dot.addEventListener('click', () => this.goToPage(i));
      this.dots.appendChild(dot);
    }
  }

  get currentPage() {
    if (this.pageCount === 0) return 0;
    if (this.track.scrollLeft >= this.maxScroll - 2) return this.pageCount - 1;
    return Math.min(this.pageCount - 1, Math.round(this.track.scrollLeft / (this.step * this.perView)));
  }

  syncControls() {
    if (this.pageCount === 0) return;

    this.prevButton.disabled = this.track.scrollLeft <= 2;
    this.nextButton.disabled = this.track.scrollLeft >= this.maxScroll - 2;

    const active = this.currentPage;
    Array.from(this.dots?.children || []).forEach((dot, i) => {
      dot.classList.toggle('is-active', i === active);
      if (i === active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  page(direction) {
    this.track.scrollBy({ left: direction * this.step * this.perView, behavior: this.behavior });
  }

  goToPage(index) {
    this.track.scrollTo({ left: index * this.step * this.perView, behavior: this.behavior });
  }

  /**
   * Page to a card when a merchant selects its block in the theme editor. Only the
   * track moves -- the editor scrolls the page to the section on its own, and
   * scrollIntoView would fight it.
   */
  revealBlock(event) {
    const slide = event.target.closest?.('[data-lsri-slide]');
    if (!slide || !this.contains(slide)) return;

    const index = this.slides.indexOf(slide);
    if (index >= 0) this.goToPage(Math.floor(index / this.perView));
  }
}

if (!customElements.get('lsri-carousel')) {
  customElements.define('lsri-carousel', LsriCarousel);
}
